import { ARENA_H, ARENA_W } from "../config.ts";
import { LevelBuilder } from "./builder.ts";
import {
  EN_ARCHER,
  EN_BEAST,
  EN_BOMBER,
  EN_HEN,
  EN_LASER,
  EN_METEOR,
  EN_NINJA,
} from "../entities/enemy.ts";
import { diagonalStream } from "./patterns.ts";
import type { Level } from "./levelTypes.ts";

/**
 * LEVEL 5 — "PYRE"
 * The final boss: a serpentine dragon whose entire body is lethal, fought
 * over the longest stage in the game. Every enemy learned on the way returns
 * exactly once, steadily through the fight — archer, hen, eyeball, ninja,
 * bomber, beast — so the finale plays like a curtain call with the dragon
 * overhead. Meteors complete their angle journey: here they arrive from
 * EVERY direction, launched off any edge of the arena straight at you.
 * Once the last minion leaves, the dragon alone goes into its FINAL FRENZY
 * — every attack at once plus two frenzy-only moves — then breaks off for
 * the FINAL ATTACK: it climbs to the top of the screen and unleashes a
 * fire blast over the whole arena before detonating. The flame is out, the
 * game is won. (Felling it early with 25 sword hits still works too.)
 */
export function buildLevel5(): Level {
  const b = new LevelBuilder(5, "PYRE", "Boss", 126);
  b.cue(0, "phase:boss");

  // The dragon arrives.
  b.at(1.5, (ctx) => ctx.dragon("enter"));

  // Meteors from every direction: a random point on any arena edge, aimed at
  // where you're standing.
  b.every(14, 104, 7, (ctx) => {
    const side = Math.floor(ctx.rng.next() * 4);
    let x: number;
    let y: number;
    if (side === 0) {
      x = ctx.rng.range(0, ctx.arenaW);
      y = -50;
    } else if (side === 1) {
      x = ctx.rng.range(0, ctx.arenaW);
      y = ctx.arenaH + 50;
    } else if (side === 2) {
      x = -50;
      y = ctx.rng.range(0, ctx.arenaH);
    } else {
      x = ctx.arenaW + 50;
      y = ctx.rng.range(0, ctx.arenaH);
    }
    const dx = ctx.playerX - x;
    const dy = ctx.playerY - y;
    const d = Math.hypot(dx, dy) || 1;
    ctx.enemy(EN_METEOR, x, y, { vx: (dx / d) * 380, vy: (dy / d) * 380 });
  });

  // --- The curtain call: each enemy returns once, in the order you met them ---
  b.at(18, (ctx) => {
    ctx.enemy(EN_ARCHER, ARENA_W * 0.5, -40, { homeX: ARENA_W * 0.5, homeY: ARENA_H * 0.18 });
  });
  b.at(34, (ctx) => {
    ctx.enemy(EN_HEN, -30, ARENA_H * 0.75, {});
  });
  b.at(48, (ctx) => {
    ctx.enemy(EN_LASER, ARENA_W + 40, ARENA_H * 0.32, {
      homeX: ARENA_W * 0.68,
      homeY: ARENA_H * 0.32,
      life: 16,
    });
  });
  b.at(64, (ctx) => {
    ctx.enemy(EN_NINJA, ARENA_W * 0.5, ARENA_H * 0.35, {});
  });
  b.at(78, (ctx) => {
    ctx.enemy(EN_BOMBER, -40, ARENA_H * 0.55, { homeX: ARENA_W * 0.28 });
  });
  b.at(94, (ctx) => {
    ctx.enemy(EN_BEAST, -16, ARENA_H * 0.5, { phase: 0, life: 9 });
  });

  // --- Phase 2 (40-80s): fire breath + dives ---
  b.cue(40, "boss:2");
  b.at(40, (ctx) => ctx.dragon("phase2"));

  // --- Phase 3 (80-104s): ember rain under the dives. No bars here — the
  // beast charge owns the lanes, so the sweeping pink walls stay out of the
  // boss fight. ---
  b.cue(80, "boss:3");
  b.at(80, (ctx) => ctx.dragon("phase3"));
  b.every(84, 103, 0.8, (ctx, t) => {
    // Ember rain: slow diagonal cinders drifting down through the fight.
    const k = Math.round((t - 84) / 0.8);
    const x = (k * 173) % ARENA_W;
    diagonalStream(ctx, x, -20, Math.PI * (k % 2 ? 0.58 : 0.42), 260, 9);
  });

  // --- FINAL FRENZY (105-117s): the curtain call is over and the last minion
  // has left the stage — just you and the dragon, every attack at once plus
  // two it has been saving: the spiral barrage and the burning tail wake. ---
  b.cue(105, "boss:frenzy");
  b.at(105, (ctx) => ctx.dragon("frenzy"));

  // --- THE FINAL ATTACK (117-124s): the dragon breaks off, climbs to the
  // top of the screen, and unleashes a fire blast over the whole arena,
  // raining down on the player… ---
  b.at(117, (ctx) => ctx.dragon("finale"));

  // --- …and burns out: the dragon detonates. The game is over. The long
  // boss outro then lets the explosion and the empty field sit for a few
  // seconds before the results appear. ---
  b.at(124, (ctx) => ctx.dragon("die"));

  // Safety margin only — the detonation above ends the level via the
  // boss-defeated flow well before the timeline runs out.
  return b.build();
}
